import { deepMerge } from '@empathybroker/deep-merge';
import { injectable } from 'inversify';
import { logger } from '../logger';
import {
  BannersConfig,
  CloseConfig,
  CoreConfig,
  DiscoveryWallConfig,
  EmpathizeConfig,
  EmpathyXConfig,
  EmptySearchConfig,
  FacetsConfig,
  FallbackFiltersConfig,
  HistoryConfig,
  IdentifierSuggestionsConfig,
  LocaleConfig,
  NextQueriesConfig,
  NextQueriesSuggestionsConfig,
  NoResultsConfig,
  OpenConfig,
  PartialsConfig,
  PopularSearchesConfig,
  PreFacetsConfig,
  RecommendationsConfig,
  RedirectionConfig,
  RedirectionsSuggestionsConfig,
  RelatedTagsConfig,
  ResultComponentsConfig,
  ResultsConfig,
  ResultsGridConfig,
  ResultsNumberConfig,
  ResultsSuggestionsConfig,
  ScrollConfig,
  ScrollToTopConfig,
  SearchBoxConfig,
  ServicesContainerConfig,
  SortConfig,
  SpellcheckConfig,
  TermSuggestionsConfig,
  TrackAddToCartConfig,
  TrackCheckoutConfig,
  TrackQueryConfig,
  URLConfig,
  UserServiceStorageConfig,
  WindowEventBinderConfig
} from '../models';
import { DeepPartial, isObjectEmptyOrNull } from '../utils';
import { ResultContainer } from './builders.interfaces';

@injectable()
export class ConfigBuilder {
  protected bannersConfig: DeepPartial<BannersConfig> = {};
  protected closeConfig: DeepPartial<CloseConfig> = {};
  protected coreConfig: DeepPartial<CoreConfig> = {};
  protected discoveryWallConfig: DeepPartial<DiscoveryWallConfig> = {};
  protected empathizeConfig: DeepPartial<EmpathizeConfig> = {};
  protected emptySearchConfig: DeepPartial<EmptySearchConfig> = {};
  protected facetsConfig: DeepPartial<FacetsConfig> = {};
  protected fallbackFiltersConfig: DeepPartial<FallbackFiltersConfig> = {};
  protected historyConfig: DeepPartial<HistoryConfig> = {};
  protected identifierSuggestionsConfig: DeepPartial<IdentifierSuggestionsConfig> = {};
  protected scrollConfig: DeepPartial<ScrollConfig> = {};
  protected localeConfig: DeepPartial<LocaleConfig> = {};
  protected nextQueriesConfig: DeepPartial<NextQueriesConfig> = {};
  protected nextQueriesSuggestions: DeepPartial<NextQueriesSuggestionsConfig> = {};
  protected noResultsConfig: DeepPartial<NoResultsConfig> = {};
  protected openConfig: DeepPartial<OpenConfig> = {};
  protected partialsConfig: DeepPartial<PartialsConfig> = {};
  protected popularSearchesConfig: DeepPartial<PopularSearchesConfig> = {};
  protected preFacetsConfig: DeepPartial<PreFacetsConfig> = {};
  protected recommendationsConfig: DeepPartial<RecommendationsConfig> = {};
  protected redirectionConfig: DeepPartial<RedirectionConfig> = {};
  protected redirectionsSuggestionsConfig: DeepPartial<RedirectionsSuggestionsConfig> = {};
  protected relatedTagsConfig: DeepPartial<RelatedTagsConfig> = {};
  protected resultsConfig: DeepPartial<ResultsConfig> = {};
  protected resultsGridConfig: DeepPartial<ResultsGridConfig> = {};
  protected resultsNumberConfig: DeepPartial<ResultsNumberConfig> = {};
  protected resultsSuggestionsConfig: DeepPartial<ResultsSuggestionsConfig> = {};
  protected scrollToTopConfig: DeepPartial<ScrollToTopConfig> = {};
  protected searchBoxConfig: DeepPartial<SearchBoxConfig> = {};
  protected servicesContainerConfig: DeepPartial<ServicesContainerConfig> = {};
  protected sortConfig: DeepPartial<SortConfig> = {};
  protected spellcheckConfig: DeepPartial<SpellcheckConfig> = {};
  protected termSuggestionsConfig: DeepPartial<TermSuggestionsConfig> = {};
  protected trackAddToCartConfig: DeepPartial<TrackAddToCartConfig> = {};
  protected trackCheckoutConfig: DeepPartial<TrackCheckoutConfig> = {};
  protected trackQueryConfig: DeepPartial<TrackQueryConfig> = {};
  protected urlConfig: DeepPartial<URLConfig> = {};
  protected userServiceStorageConfig: DeepPartial<UserServiceStorageConfig> = {};
  protected windowEventBinderConfig: DeepPartial<WindowEventBinderConfig> = {};

  build(): DeepPartial<EmpathyXConfig> {
    return this.getEmpathyXConfig();
  }

  setBannersConfig(bannersConfig: DeepPartial<BannersConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.bannersConfig, 'Banners');
    deepMerge(this.bannersConfig, bannersConfig);
    return this;
  }

  setCloseConfig(closeConfig: DeepPartial<CloseConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.closeConfig, 'Close');
    deepMerge(this.closeConfig, closeConfig);
    return this;
  }

  setUserServiceStorageConfig(userServiceStorageConfig: DeepPartial<UserServiceStorageConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.userServiceStorageConfig, 'User service storage');
    deepMerge(this.userServiceStorageConfig, userServiceStorageConfig);
    return this;
  }

  setCoreConfig(coreConfig: DeepPartial<CoreConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.coreConfig, 'Core');
    deepMerge(this.coreConfig, coreConfig);
    return this;
  }

  setDiscoveryWallConfig(discoveryWallConfig: DeepPartial<DiscoveryWallConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.discoveryWallConfig, 'DiscoveryWall');
    deepMerge(this.discoveryWallConfig, discoveryWallConfig);
    return this;
  }

  setEmpathizeConfig(empathizeConfig: DeepPartial<EmpathizeConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.empathizeConfig, 'Empathize');
    deepMerge(this.empathizeConfig, empathizeConfig);
    return this;
  }

  setEmptySearchConfig(emptySearchConfig: DeepPartial<EmptySearchConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.emptySearchConfig, 'Empty search');
    deepMerge(this.emptySearchConfig, emptySearchConfig);
    return this;
  }

  setFacetsConfig(facetsConfig: DeepPartial<FacetsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.facetsConfig, 'Facets');
    deepMerge(this.facetsConfig, facetsConfig);
    return this;
  }

  setFallbackFiltersConfig(fallbackFiltersConfig: DeepPartial<FallbackFiltersConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.fallbackFiltersConfig, 'Fallback Filters');
    deepMerge(this.fallbackFiltersConfig, fallbackFiltersConfig);
    return this;
  }

  setRedirectionConfig(redirectionConfig: DeepPartial<RedirectionConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.redirectionConfig, 'Redirection');
    deepMerge(this.redirectionConfig, redirectionConfig);
    return this;
  }

  setHistoryConfig(historyConfig: DeepPartial<HistoryConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.historyConfig, 'History');
    deepMerge(this.historyConfig, historyConfig);
    return this;
  }

  setIdentifierSuggestionsConfig(identifierSuggestionsConfig: DeepPartial<IdentifierSuggestionsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.identifierSuggestionsConfig, 'Identifier suggestions');
    deepMerge(this.identifierSuggestionsConfig, identifierSuggestionsConfig);
    return this;
  }

  setScrollConfig(scrollConfig: DeepPartial<ScrollConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.scrollConfig, 'Scroll');
    deepMerge(this.scrollConfig, scrollConfig);
    return this;
  }

  setLocaleConfig(localeConfig: DeepPartial<LocaleConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.localeConfig, 'Locale');
    deepMerge(this.localeConfig, localeConfig);
    return this;
  }

  setNextQueriesConfig(nextQueriesConfig: DeepPartial<NextQueriesConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.nextQueriesConfig, 'NextQueries');
    deepMerge(this.nextQueriesConfig, nextQueriesConfig);
    return this;
  }

  setNextQueriesSuggestionsConfig(nextQueriesSuggestions: DeepPartial<NextQueriesSuggestionsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.nextQueriesSuggestions, 'NextQueries suggestions');
    deepMerge(this.nextQueriesSuggestions, nextQueriesSuggestions);
    return this;
  }

  setNoResultsConfig(noResultsConfig: DeepPartial<NoResultsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.noResultsConfig, 'No results');
    deepMerge(this.noResultsConfig, noResultsConfig);
    return this;
  }

  setOpenConfig(openConfig: DeepPartial<OpenConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.openConfig, 'Open');
    deepMerge(this.openConfig, openConfig);
    return this;
  }

  setPartialsConfig(partialsConfig: DeepPartial<PartialsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.partialsConfig, 'Partials');
    deepMerge(this.partialsConfig, partialsConfig);
    return this;
  }

  setPopularSearchesConfig(popularSearchesConfig: DeepPartial<PopularSearchesConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.popularSearchesConfig, 'Popular searches');
    deepMerge(this.popularSearchesConfig, popularSearchesConfig);
    return this;
  }

  setPreFacetsConfig(preFacetsConfig: DeepPartial<PreFacetsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.preFacetsConfig, 'Pre facets');
    deepMerge(this.preFacetsConfig, preFacetsConfig);
    return this;
  }

  setRecommendationsConfig(recommendationsConfig: DeepPartial<RecommendationsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.recommendationsConfig, 'Recommendations');
    deepMerge(this.recommendationsConfig, recommendationsConfig);
    return this;
  }

  setRedirectionsSuggestionsConfig(redirectionsSuggestionsConfig: DeepPartial<RedirectionsSuggestionsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.redirectionsSuggestionsConfig, 'Redirections Suggestions Direct');
    deepMerge(this.redirectionsSuggestionsConfig, redirectionsSuggestionsConfig);
    return this;
  }

  setRelatedTagsConfig(relatedTagsConfig: DeepPartial<RelatedTagsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.relatedTagsConfig, 'RelatedTags');
    deepMerge(this.relatedTagsConfig, relatedTagsConfig);
    return this;
  }

  // TODO Re-think resultContainer name parameter, it could be confused with the injection container.
  setResultsConfig(resultConfig: DeepPartial<ResultComponentsConfig>, resultFeature: ResultContainer = 'all',
    type = 'default'): ConfigBuilder {
    if (!this.resultsConfig[resultFeature]) {
      this.resultsConfig[resultFeature] = {};
    }
    if (!this.resultsConfig[resultFeature][type]) {
      this.resultsConfig[resultFeature][type] = {};
    }
    const config = resultConfig.template ? resultConfig : { template: { children: resultConfig } };
    deepMerge(this.resultsConfig[resultFeature][type], config);
    return this;
  }

  setResultsGridConfig(resultsConfig: DeepPartial<ResultsGridConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.resultsGridConfig, 'Results');
    deepMerge(this.resultsGridConfig, resultsConfig);
    return this;
  }

  setResultsNumberConfig(resultsNumberConfig: DeepPartial<ResultsNumberConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.resultsNumberConfig, 'Results number');
    deepMerge(this.resultsNumberConfig, resultsNumberConfig);
    return this;
  }

  setResultsSuggestionsConfig(resultsSuggestionsConfig: DeepPartial<ResultsSuggestionsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.windowEventBinderConfig, 'Results suggestions');
    deepMerge(this.resultsSuggestionsConfig, resultsSuggestionsConfig);
    return this;
  }

  setScrollToTopConfig(scrollToTopConfig: DeepPartial<ScrollToTopConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.scrollToTopConfig, 'Scroll to top');
    deepMerge(this.scrollToTopConfig, scrollToTopConfig);
    return this;
  }

  setSearchBoxConfig(searchBoxConfig: DeepPartial<SearchBoxConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.searchBoxConfig, 'Search box');
    deepMerge(this.searchBoxConfig, searchBoxConfig);
    return this;
  }

  setServicesContainerConfig(servicesContainerConfig: DeepPartial<ServicesContainerConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.servicesContainerConfig, 'Services');
    deepMerge(this.servicesContainerConfig, servicesContainerConfig);
    return this;
  }

  setSortConfig(sortConfig: DeepPartial<SortConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.sortConfig, 'Sort');
    deepMerge(this.sortConfig, sortConfig);
    return this;
  }

  setSpellcheckConfig(spellcheckConfig: DeepPartial<SpellcheckConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.spellcheckConfig, 'Spellcheck');
    deepMerge(this.spellcheckConfig, spellcheckConfig);
    return this;
  }

  setSuggestionsConfig(suggestionsConfig: DeepPartial<TermSuggestionsConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.termSuggestionsConfig, 'Suggestions');
    deepMerge(this.termSuggestionsConfig, suggestionsConfig);
    return this;
  }

  setTrackAddToCartConfig(trackAddToCartConfig: DeepPartial<TrackAddToCartConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.trackAddToCartConfig, 'Add to cart tracking');
    deepMerge(this.trackAddToCartConfig, trackAddToCartConfig);
    return this;
  }

  setTrackCheckoutConfig(trackCheckoutConfig: DeepPartial<TrackCheckoutConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.trackCheckoutConfig, 'Add to checkout tracking');
    deepMerge(this.trackCheckoutConfig, trackCheckoutConfig);
    return this;
  }

  setTrackQueryConfig(trackQueryConfig: DeepPartial<TrackQueryConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.trackQueryConfig, 'Query tracking');
    deepMerge(this.trackQueryConfig, trackQueryConfig);
    return this;
  }

  setUrlConfig(urlConfig: DeepPartial<URLConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.urlConfig, 'URL');
    deepMerge(this.urlConfig, urlConfig);
    return this;
  }

  setWindowEventBinderConfig(windowEventBinderConfig: DeepPartial<WindowEventBinderConfig>): ConfigBuilder {
    this.warnIfConfigSet(this.windowEventBinderConfig, 'Window event binder');
    deepMerge(this.windowEventBinderConfig, windowEventBinderConfig);
    return this;
  }

  protected warnIfConfigSet(config: any, configName: string) {
    if (!isObjectEmptyOrNull(config)) {
      logger.warn('[ConfigBuilder]', `Configuration for '${ configName }' has been already set: merging both of them...`);
    }
  }

  private getEmpathyXConfig(): DeepPartial<EmpathyXConfig> {
    return {
      banners: this.bannersConfig,
      close: this.closeConfig,
      container: {
        services: this.servicesContainerConfig
      },
      core: this.coreConfig,
      discoveryWall: this.discoveryWallConfig,
      empathize: this.empathizeConfig,
      emptySearch: this.emptySearchConfig,
      facets: this.facetsConfig,
      scroll: this.scrollConfig,
      fallbackFilters: this.fallbackFiltersConfig,
      history: this.historyConfig,
      identifierSuggestions: this.identifierSuggestionsConfig,
      locale: this.localeConfig,
      nextQueries: this.nextQueriesConfig,
      nextQueriesSuggestions: this.nextQueriesSuggestions,
      noResults: this.noResultsConfig,
      open: this.openConfig,
      partialResults: this.partialsConfig,
      popularSearches: this.popularSearchesConfig,
      preFacets: this.preFacetsConfig,
      recommendations: this.recommendationsConfig,
      redirection: this.redirectionConfig,
      redirectionsSuggestions: this.redirectionsSuggestionsConfig,
      relatedTags: this.relatedTagsConfig,
      results: this.resultsConfig,
      resultsGrid: this.resultsGridConfig,
      resultsNumber: this.resultsNumberConfig,
      resultsSuggestions: this.resultsSuggestionsConfig,
      scrollToTop: this.scrollToTopConfig,
      sort: this.sortConfig,
      searchBox: this.searchBoxConfig,
      termSuggestions: this.termSuggestionsConfig,
      tracking: {
        addToCart: this.trackAddToCartConfig,
        checkout: this.trackCheckoutConfig,
        query: this.trackQueryConfig
      },
      url: this.urlConfig,
      userServiceStorage: this.userServiceStorageConfig,
      windowEventBinder: this.windowEventBinderConfig
    };
  }
}
